import { Button, Spinner } from '@ohear/ui'
import { Link } from '@tanstack/react-router'
/**
 * NotificationDropdown — the panel that opens beneath the bell.
 *
 * Pulls the most recent ~10 notifications regardless of read state so the
 * user can scan recent activity at a glance. The full /account/notifications
 * page provides filtering and infinite scroll.
 */
import { useEffect, useRef } from 'react'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../lib/api/notifications'
import { NotificationItem } from './NotificationItem'

export interface NotificationDropdownProps {
  open: boolean
  onClose: () => void
}

export function NotificationDropdown({ open, onClose }: NotificationDropdownProps) {
  const list = useNotifications({ limit: 10 })
  const markOne = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const ref = useRef<HTMLDivElement>(null)

  // Outside-click to close.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open, onClose])

  if (!open) return null

  const items = list.data?.items ?? []
  const unreadCount = list.data?.unread_count ?? 0

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Notifications"
      className="absolute right-0 top-[calc(100%+4px)] z-20 w-[360px] max-w-[92vw] rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-2)] shadow-lg"
    >
      <header className="flex items-center justify-between gap-2 border-b border-[var(--border)] px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold">Notifications</span>
          {unreadCount > 0 ? (
            <span className="text-[11px] text-[var(--text-2)]">{unreadCount} unread</span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="ghost"
          disabled={unreadCount === 0}
          loading={markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          Mark all read
        </Button>
      </header>

      <div role="list" className="max-h-[420px] overflow-y-auto">
        {list.isLoading ? (
          <div className="flex items-center gap-2 text-[var(--text-2)] p-3 text-[13px]">
            <Spinner /> Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="p-4 text-[13px] text-[var(--text-2)]">You're all caught up.</div>
        ) : (
          items.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              dense
              isMarking={markOne.isPending && markOne.variables === n.id}
              onMarkRead={(id) => markOne.mutate(id)}
            />
          ))
        )}
      </div>

      <footer className="border-t border-[var(--border)] px-3 py-2 text-right">
        <Link
          to="/account/notifications"
          onClick={onClose}
          className="text-[12px] text-[var(--accent)] no-underline hover:underline"
        >
          View all notifications
        </Link>
      </footer>
    </div>
  )
}
